/**
 * Iter122 — Admin: Field Reports Review
 *
 * Admin module to view, edit, approve, deny, or send back completed field
 * reports. Also manages team time entry approvals.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { FileSignature, CheckCircle2, XCircle, RotateCcw, RefreshCw, Clock, User } from 'lucide-react';

const dt = (v) => v ? new Date(v).toLocaleString() : '—';
const fmtHMS = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

export default function AdminFieldReports() {
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [reports, setReports] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rr, tr] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/field-reports`, { headers: H }),
        fetch(`${BACKEND_URL}/api/admin/time-entries?status=COMPLETED`, { headers: H }),
      ]);
      setReports(rr.ok ? await rr.json() : []);
      setTimeEntries(tr.ok ? await tr.json() : []);
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openReport = async (id) => {
    const r = await fetch(`${BACKEND_URL}/api/field-assignments/${id}/report`, { headers: H });
    if (r.ok) { setSelected(await r.json()); setReviewNotes(''); }
  };

  const review = async (action) => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/field-assignments/${selected.id}/review`, {
        method: 'POST', headers: JH, body: JSON.stringify({ action, notes: reviewNotes }),
      });
      if (!r.ok) return toast({ title: 'Failed', variant: 'destructive' });
      toast({ title: `Report ${action.toLowerCase().replace('_', ' ')}` });
      setSelected(null); setReviewNotes('');
      await load();
    } finally { setBusy(false); }
  };

  const approveTime = async (id) => {
    const r = await fetch(`${BACKEND_URL}/api/time-entries/${id}/approve`, { method: 'POST', headers: JH });
    if (r.ok) { toast({ title: 'Approved' }); await load(); }
  };
  const denyTime = async (id) => {
    const reason = window.prompt('Denial reason (optional):') || '';
    const r = await fetch(`${BACKEND_URL}/api/time-entries/${id}/deny`, {
      method: 'POST', headers: JH, body: JSON.stringify({ reason }),
    });
    if (r.ok) { toast({ title: 'Denied' }); await load(); }
  };

  return (
    <div className="space-y-6" data-testid="admin-field-reports-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center">
            <FileSignature className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <div className="text-xs text-indigo-400 uppercase tracking-wider font-mono">Admin · Review</div>
            <h1 className="text-2xl font-bold text-white">Field Reports & Time Approvals</h1>
            <p className="text-slate-400 text-sm">Approve, deny, or send back completed field reports and team time entries.</p>
          </div>
        </div>
        <Button variant="ghost" onClick={load} className="text-slate-300">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <Tabs defaultValue="reports">
        <TabsList className="bg-[#131A2A] border-[#334155]">
          <TabsTrigger value="reports" data-testid="admin-tab-reports">Field Reports ({reports.length})</TabsTrigger>
          <TabsTrigger value="time" data-testid="admin-tab-time">Time Entries ({timeEntries.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="reports" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="bg-[#131A2A] border-[#334155] lg:col-span-1">
              <CardHeader><CardTitle className="text-sm text-white">Completed Assignments</CardTitle></CardHeader>
              <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
                {reports.length === 0 ? <div className="text-xs text-slate-500 text-center py-6">Nothing to review.</div> : reports.map(r => (
                  <button key={r.id} onClick={() => openReport(r.id)}
                    className={`w-full text-left p-2 rounded border ${selected?.id === r.id ? 'bg-indigo-900/40 border-indigo-500' : 'bg-slate-900 border-slate-700 hover:border-slate-600'}`}
                    data-testid={`admin-report-row-${r.id}`}>
                    <div className="text-sm text-white truncate">{r.case_number || r.case_id} · {r.purpose?.replace(/_/g, ' ')}</div>
                    <div className="text-xs text-slate-400"><User className="w-3 h-3 inline mr-0.5" /> {r.investigator_name}</div>
                    <div className="text-[10px] text-slate-500">Ended: {dt(r.end_time)}</div>
                    {r.review_status && <Badge className={`mt-1 text-[9px] ${r.review_status === 'APPROVED' ? 'bg-blue-500/20 text-blue-300' : r.review_status === 'DENIED' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>{r.review_status}</Badge>}
                  </button>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-[#131A2A] border-[#334155] lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  Report Detail
                  {selected?.review_status && <Badge className={selected.review_status === 'APPROVED' ? 'bg-blue-500/20 text-blue-300' : selected.review_status === 'DENIED' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}>{selected.review_status}</Badge>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!selected ? <div className="text-sm text-slate-500 text-center py-8">Select a report to review.</div> : (
                  <div className="space-y-3">
                    <div className="text-sm text-slate-300">
                      <b>Case:</b> {selected.case_number} · <b>Investigator:</b> {selected.investigator_name}<br />
                      <b>Purpose:</b> {selected.purpose?.replace(/_/g, ' ')} · <b>Location:</b> {selected.assignment_location || '—'}<br />
                      <b>Started:</b> {dt(selected.start_time)} · <b>Ended:</b> {dt(selected.end_time)}
                    </div>
                    <div className="text-xs text-slate-400 max-h-[300px] overflow-y-auto border border-slate-700 rounded p-2 bg-slate-900/50">
                      {Object.entries(selected.report_sections || {}).map(([sec, rows]) => (
                        <div key={sec} className="mb-2">
                          <div className="text-white font-semibold uppercase text-[10px] tracking-wide">{sec.replace(/_/g, ' ')} ({rows?.length || 0})</div>
                          {(rows || []).map((row, idx) => (
                            <div key={row.id || idx} className="text-slate-300">· {Object.entries(row).filter(([k, v]) => !['id', 'source', 'added_by', 'added_at'].includes(k) && v).map(([k, v]) => `${k}: ${v}`).join(' | ')}</div>
                          ))}
                        </div>
                      ))}
                      {(selected.events || []).length > 0 && (
                        <div className="mt-2">
                          <div className="text-white font-semibold uppercase text-[10px] tracking-wide">Events ({selected.events.length})</div>
                          {selected.events.map(e => (
                            <div key={e.id} className="text-slate-300">· [{e.event_type}] {dt(e.timestamp)} — {e.content}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                      placeholder="Review notes (optional)" className="bg-slate-800 border-slate-700 text-white" rows={2} data-testid="review-notes-input" />
                    <div className="flex gap-2 flex-wrap">
                      <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => review('APPROVE')} disabled={busy} data-testid="review-approve-btn">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button className="bg-red-600 hover:bg-red-500 text-white" onClick={() => review('DENY')} disabled={busy} data-testid="review-deny-btn">
                        <XCircle className="w-4 h-4 mr-1" /> Deny
                      </Button>
                      <Button variant="outline" className="border-amber-500/40 !text-amber-300" onClick={() => review('SEND_BACK')} disabled={busy} data-testid="review-sendback-btn">
                        <RotateCcw className="w-4 h-4 mr-1" /> Send Back
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="time" className="mt-4">
          <Card className="bg-[#131A2A] border-[#334155]">
            <CardContent className="p-0">
              {timeEntries.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-500">No completed time entries awaiting approval.</div>
              ) : (
                <div className="divide-y divide-slate-800">
                  {timeEntries.map(e => (
                    <div key={e.id} className="p-3 flex items-center justify-between flex-wrap gap-2" data-testid={`admin-time-${e.id}`}>
                      <div>
                        <div className="text-sm text-white">
                          {e.activity?.replace(/_/g, ' ')} <Badge className="ml-1 text-[10px] bg-slate-700 text-slate-200">{e.status}</Badge>
                          {e.approval_status && <Badge className={`ml-1 text-[10px] ${e.approval_status === 'APPROVED' ? 'bg-blue-500/20 text-blue-300' : 'bg-red-500/20 text-red-300'}`}>{e.approval_status}</Badge>}
                        </div>
                        <div className="text-xs text-slate-400 flex items-center gap-2">
                          <User className="w-3 h-3" /> {e.investigator_name} · <Clock className="w-3 h-3" /> {fmtHMS(e.duration_seconds)} · Case {e.case_number || e.case_id}
                        </div>
                        <div className="text-[10px] text-slate-500">Start {dt(e.start_time)} · End {dt(e.end_time)}</div>
                      </div>
                      <div className="flex gap-1">
                        {e.approval_status !== 'APPROVED' && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => approveTime(e.id)} data-testid={`admin-time-approve-${e.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                          </Button>
                        )}
                        {e.approval_status !== 'DENIED' && (
                          <Button size="sm" variant="outline" className="border-red-500/40 !text-red-300" onClick={() => denyTime(e.id)} data-testid={`admin-time-deny-${e.id}`}>
                            <XCircle className="w-3 h-3 mr-1" /> Deny
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
